import type { MathNode } from 'mathjs';
import type { ExpressionOperatorRegistry } from './ExpressionOperatorRegistry.ts';
import { all, create } from 'mathjs';
import { SeededRandom } from '../SeededRandom.ts';
import {
  ALLOWED_NODE_TYPES,
  ALLOWED_OPERATORS,
  FORBIDDEN_PROPERTIES,
  SAFE_CONSTANTS,
} from './expressionLanguage.ts';
import { createBuiltinExpressionOperatorRegistry } from './ExpressionOperatorRegistry.ts';

// The declarative expression language permits comparing string fields such as
// entity ids (for example `entity.id == first(entity_customer).id`). mathjs
// treats `==` / `!=` as numeric coercion and throws on non-numeric strings, so
// we evaluate expressions with a dedicated instance whose equality operators
// compare strings strictly while delegating every other case to mathjs.
const stringSafeMath = create(all);
const mathEqual = stringSafeMath.equal as unknown as (
  left: unknown,
  right: unknown,
) => unknown;
const mathUnequal = stringSafeMath.unequal as unknown as (
  left: unknown,
  right: unknown,
) => unknown;

stringSafeMath.import({
  equal(left: unknown, right: unknown): unknown {
    if (typeof left === 'string' || typeof right === 'string')
      return left === right;
    return mathEqual(left, right);
  },
  unequal(left: unknown, right: unknown): unknown {
    if (typeof left === 'string' || typeof right === 'string')
      return left !== right;
    return mathUnequal(left, right);
  },
}, { override: true });

interface FunctionLikeNode extends MathNode {
  name?: string;
  op?: string;
}

interface IndexLikeNode extends MathNode {
  dimensions?: MathNode[];
}

export type ExpressionScope = Record<string, unknown>;

export class SafeExpressionEngine {
  private readonly cache = new Map<string, MathNode>();
  private readonly random: SeededRandom;

  constructor(
    seed: number,
    private readonly allowedSymbols: Set<string>,
    private readonly operators: ExpressionOperatorRegistry
      = createBuiltinExpressionOperatorRegistry(),
  ) {
    this.random = new SeededRandom(seed);
    const collision = [...allowedSymbols].find(symbol => operators.has(symbol));
    if (collision)
      throw new Error(`表达式符号与运算符冲突：${collision}`);
  }

  validate(expression: string): void {
    this.getNode(expression);
  }

  evaluate(expression: string, scope: ExpressionScope): unknown {
    const node = this.getNode(expression);
    try {
      const value = node.evaluate({
        ...this.operators.createScope({ random: this.random }),
        ...scope,
      });
      return this.assertSafeValue(value);
    }
    catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      throw new Error(`表达式“${expression}”执行失败：${message}`, {
        cause: error,
      });
    }
  }

  getRandomState(): number {
    return this.random.getState();
  }

  restoreRandomState(state: number): void {
    this.random.restoreState(state);
  }

  referencedSymbols(expression: string): Set<string> {
    const symbols = new Set<string>();
    this.getNode(expression).traverse((child, path, parent) => {
      if (child.type !== 'SymbolNode')
        return;
      const name = (child as FunctionLikeNode).name ?? '';
      const isFunctionName = parent?.type === 'FunctionNode' && path === 'fn';
      if (!isFunctionName && !SAFE_CONSTANTS.has(name))
        symbols.add(name);
    });
    return symbols;
  }

  private getNode(expression: string): MathNode {
    const cached = this.cache.get(expression);
    if (cached)
      return cached;

    const node = stringSafeMath.parse(expression);
    node.traverse((child, path, parent) => {
      if (!ALLOWED_NODE_TYPES.has(child.type))
        throw new Error(`表达式包含不允许的语法：${child.type}`);

      const typedChild = child as FunctionLikeNode;
      if (
        child.type === 'OperatorNode'
        && (!typedChild.op || !ALLOWED_OPERATORS.has(typedChild.op))
      ) {
        throw new Error(`表达式运算符不受支持：${typedChild.op ?? 'unknown'}`);
      }

      if (child.type === 'FunctionNode') {
        const functionName = typedChild.name;
        if (!functionName || !this.operators.has(functionName))
          throw new Error(`表达式函数不受支持：${functionName ?? 'dynamic'}`);
      }

      if (child.type === 'SymbolNode') {
        const name = typedChild.name ?? '';
        const isFunctionName = parent?.type === 'FunctionNode' && path === 'fn';
        if (
          isFunctionName
          && !this.operators.has(name)
        ) {
          throw new Error(`表达式函数不受支持：${name}`);
        }
        if (
          !isFunctionName
          && !SAFE_CONSTANTS.has(name)
          && !this.allowedSymbols.has(name)
        ) {
          throw new Error(`表达式引用了未声明符号：${name}`);
        }
      }

      if (child.type === 'ConstantNode') {
        const value = (child as unknown as { value: unknown }).value;
        if (typeof value === 'string' && FORBIDDEN_PROPERTIES.has(value))
          throw new Error(`表达式属性不受支持：${value}`);
      }

      if (child.type === 'IndexNode') {
        const dimensions = (child as IndexLikeNode).dimensions ?? [];
        if (dimensions.some(dimension => dimension.type !== 'ConstantNode'))
          throw new Error('表达式不允许动态属性访问');
      }
    });
    this.cache.set(expression, node);
    return node;
  }

  private assertSafeValue(value: unknown): unknown {
    if (
      value === null
      || typeof value === 'string'
      || typeof value === 'boolean'
    ) {
      return value;
    }
    if (typeof value === 'number') {
      if (!Number.isFinite(value))
        throw new Error('表达式结果不是有限数值');
      return value;
    }
    if (Array.isArray(value))
      return value.map(item => this.assertSafeValue(item));
    if (value && typeof value === 'object') {
      const prototype = Object.getPrototypeOf(value);
      if (prototype !== Object.prototype && prototype !== null)
        throw new Error('表达式返回了不安全对象');
      return Object.fromEntries(
        Object.entries(value).map(([key, item]) => {
          if (FORBIDDEN_PROPERTIES.has(key))
            throw new Error(`表达式返回了不安全属性：${key}`);
          return [key, this.assertSafeValue(item)];
        }),
      );
    }
    throw new Error('表达式返回了不支持的值');
  }
}
